import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilState, useRecoilValue } from 'recoil';

import { bugoDataAtomFamily } from '@shared/api/bugo/bugo.atom';
import { useBugoHook } from '@shared/api/bugo/bugo.hook';
import {
  ThanksPrintConfig,
  ThanksPrintTemplate,
} from '@shared/api/giftItemBrand/giftItemBrand.interface';
import { Image } from '@shared/api/media/media.interface';

import { GiftThanksPrintPreview } from './GiftThanksPrintPreview';
import { giftThanksPrintAtom } from '../../state/giftThanksPrint.atom';
import {
  clampThanksMessage,
  fillThanksMessage,
  getThanksSenderName,
  parseThanksSender,
  THANKS_FALLBACK_NAME,
  THANKS_MESSAGE_MAX_CHARS,
  THANKS_MESSAGE_PRESETS,
  THANKS_SENDER_SUFFIXES,
  ThanksSenderSuffix,
  thanksMessageCharCount,
} from '../../utils/thanksPrintMessage';

interface GiftThanksPrintSectionProps {
  thanksPrint: ThanksPrintConfig<Image>;
  brandId: string;
  bugoId: string | null;
  /** 모달 등에 임베드 시 상단 구분선/여백 제거 (주문/결제 수정용) */
  embedded?: boolean;
}

const MIN_SWIPE = 50;

export const GiftThanksPrintSection: FC<GiftThanksPrintSectionProps> =
  function GiftThanksPrintSection({ thanksPrint, brandId, bugoId, embedded = false }) {
    // 상주/고인 정보
    useBugoHook(bugoId ?? '', !bugoId);
    const bugo = useRecoilValue(bugoDataAtomFamily(bugoId ?? ''));
    const member = bugo?.member ?? null;
    // ### 치환용: 상주의 고인에 대한 관계 + 고인 성별
    const relationType = member?.relationType;
    const deceasedSex = bugo?.fevent?.deceasedInfo?.sex;

    const [selection, setSelection] = useRecoilState(giftThanksPrintAtom);

    // 노출 템플릿 (enabled !== false, order 순)
    const templates = useMemo<ThanksPrintTemplate<Image>[]>(() => {
      return (thanksPrint.templates ?? [])
        .filter((t) => t.enabled !== false)
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }, [thanksPrint.templates]);

    const [templateIndex, setTemplateIndex] = useState(0);
    const [presetIndex, setPresetIndex] = useState(0);
    const [message, setMessage] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [isMessageModified, setIsMessageModified] = useState(false);
    const [senderName, setSenderName] = useState(THANKS_FALLBACK_NAME);
    const [senderSuffix, setSenderSuffix] = useState<ThanksSenderSuffix>('배상');
    const [ready, setReady] = useState(false);

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);

    // 초기화 1회: 보존된 값 우선(상품 무관 공통 내용), 없으면 member 기반 기본값
    const initRef = useRef(false);
    useEffect(() => {
      if (initRef.current) return;

      if (selection) {
        initRef.current = true;
        setMessage(selection.message);
        // 레거시 합성값/누락 suffix 방어적으로 정규화
        const parsed = parseThanksSender(selection.senderName, selection.senderSuffix);
        setSenderName(parsed.name);
        setSenderSuffix(parsed.suffix);
        setIsMessageModified(true);
        // 같은 브랜드면 이전에 고른 디자인(템플릿) 복원
        if (selection.brandId === brandId && selection.templateId) {
          const i = templates.findIndex((t) => t._id === selection.templateId);
          if (i >= 0) setTemplateIndex(i);
        }
        setReady(true);
        return;
      }

      if (member) {
        initRef.current = true;
        setMessage(
          clampThanksMessage(
            fillThanksMessage(THANKS_MESSAGE_PRESETS[0] ?? '', relationType, deceasedSex),
          ),
        );
        setSenderName(getThanksSenderName(member));
        setReady(true);
      }
    }, [selection, member, brandId, templates]);

    // 선택 인덱스 범위 보정
    useEffect(() => {
      if (templateIndex > templates.length - 1) setTemplateIndex(0);
    }, [templates.length, templateIndex]);

    const composedSender = `${senderName.trim() || THANKS_FALLBACK_NAME} ${senderSuffix}`;
    const selectedTemplate = templates[templateIndex];

    // 입력값을 atom에 보존 (주문하지 않아도 유지 → 다른 상품에서도 이어 사용)
    useEffect(() => {
      if (!ready) return;
      setSelection({
        message,
        senderName,
        senderSuffix,
        brandId,
        templateId: selectedTemplate?._id,
      });
    }, [
      ready,
      message,
      senderName,
      senderSuffix,
      brandId,
      selectedTemplate,
      setSelection,
    ]);

    // 편집 시 textarea 높이 자동조절
    const adjustHeight = useCallback(() => {
      const el = textareaRef.current;
      if (el) {
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
      }
    }, []);
    useEffect(() => {
      if (isEditing) adjustHeight();
    }, [isEditing, message, adjustHeight]);

    const handlePresetSelect = (idx: number) => {
      setPresetIndex(idx);
      setMessage(
        clampThanksMessage(
          fillThanksMessage(THANKS_MESSAGE_PRESETS[idx] ?? '', relationType, deceasedSex),
        ),
      );
      setIsMessageModified(false);
    };

    const onTouchStart = (e: React.TouchEvent) => {
      setTouchEnd(null);
      setTouchStart(e.targetTouches[0]?.clientX ?? null);
    };
    const onTouchMove = (e: React.TouchEvent) => {
      setTouchEnd(e.targetTouches[0]?.clientX ?? null);
    };
    const onTouchEnd = () => {
      if (touchStart === null || touchEnd === null) return;
      const distance = touchStart - touchEnd;
      if (distance > MIN_SWIPE) {
        setTemplateIndex((p) => (p === templates.length - 1 ? 0 : p + 1));
      } else if (distance < -MIN_SWIPE) {
        setTemplateIndex((p) => (p === 0 ? templates.length - 1 : p - 1));
      }
    };

    if (templates.length === 0) return null;

    const charCount = thanksMessageCharCount(message);

    return (
      <div
        className={embedded ? 'px-4 pb-6 pt-1' : 'border-t-8 border-gray-50 px-4 py-6'}
      >
        {/* 헤더 */}
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-stone-100 text-base">
            ✉️
          </span>
          <div className="min-w-0">
            <h3 className="text-base font-bold text-stone-800">감사 메시지 인쇄</h3>
            <p className="truncate text-xs text-stone-500">
              감사글을 정성스럽게 인쇄해 함께 보내드려요
            </p>
          </div>
        </div>

        <div className="mt-5 space-y-6">
          {thanksPrint.guideText && (
            <p className="whitespace-pre-line break-keep rounded-lg bg-stone-50 px-3 py-2.5 text-[13px] leading-relaxed text-stone-600">
              {thanksPrint.guideText}
            </p>
          )}

          {/* 1. 디자인 선택 + 라이브 미리보기 (스와이프) */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="flex items-center gap-1.5 text-sm font-semibold text-gray-800">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-stone-100 text-[11px] font-bold text-stone-600">
                  1
                </span>
                디자인 선택
                {templates.length > 1 && (
                  <span className="text-xs font-normal text-gray-400">
                    (좌우로 넘겨보세요)
                  </span>
                )}
              </p>
              {templates.length > 1 && (
                <span className="text-xs font-medium text-gray-400">
                  {templateIndex + 1} / {templates.length}
                </span>
              )}
            </div>

            <div className="relative">
              <GiftThanksPrintPreview
                template={selectedTemplate}
                message={message}
                sender={composedSender}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              />

              {templates.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() =>
                      setTemplateIndex((p) => (p === 0 ? templates.length - 1 : p - 1))
                    }
                    className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/25 p-2 text-white backdrop-blur-sm"
                  >
                    ◀
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setTemplateIndex((p) => (p === templates.length - 1 ? 0 : p + 1))
                    }
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/25 p-2 text-white backdrop-blur-sm"
                  >
                    ▶
                  </button>
                </>
              )}
            </div>

            {templates.length > 1 && (
              <div className="mt-2.5 flex items-center justify-center gap-1.5">
                {templates.map((t, idx) => (
                  <button
                    key={t._id ?? idx}
                    type="button"
                    onClick={() => setTemplateIndex(idx)}
                    className={`h-1.5 rounded-full transition-all ${
                      idx === templateIndex ? 'w-5 bg-stone-500' : 'w-1.5 bg-stone-200'
                    }`}
                    aria-label={`템플릿 ${idx + 1}`}
                  />
                ))}
              </div>
            )}
            {selectedTemplate?.name && (
              <p className="mt-1 text-center text-xs text-gray-500">
                {selectedTemplate.name}
              </p>
            )}
          </div>

          {/* 2. 감사글 선택 */}
          <div>
            <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-gray-800">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-stone-100 text-[11px] font-bold text-stone-600">
                2
              </span>
              감사글 선택
            </p>
            <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
              {THANKS_MESSAGE_PRESETS.map((preset, idx) => {
                const filled = fillThanksMessage(preset, relationType, deceasedSex);
                const isActive = idx === presetIndex && !isMessageModified;
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handlePresetSelect(idx)}
                    className={`w-40 shrink-0 rounded-xl border p-3 text-left text-xs leading-snug transition-colors ${
                      isActive
                        ? 'border-stone-400 bg-stone-50 text-gray-900 ring-1 ring-stone-200'
                        : 'border-gray-200 bg-white text-gray-500'
                    }`}
                  >
                    <span className="line-clamp-4 whitespace-pre-line break-keep">
                      {filled}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* 선택된 감사글 — 탭하여 직접 수정 (4줄 / 100자 제한) */}
            <div className="mt-3">
              <div className="mb-1 flex items-center justify-between">
                <span className="text-xs font-medium text-gray-500">
                  내용을 직접 수정할 수 있어요 (4줄 이내)
                </span>
                {isEditing ? (
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="rounded-md bg-stone-600 px-2.5 py-1 text-xs font-medium text-white"
                  >
                    완료
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setIsEditing(true)}
                    className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600"
                  >
                    수정
                  </button>
                )}
              </div>
              {isEditing ? (
                <textarea
                  ref={textareaRef}
                  value={message}
                  onChange={(e) => {
                    setMessage(clampThanksMessage(e.target.value));
                    setIsMessageModified(true);
                  }}
                  onInput={adjustHeight}
                  autoFocus
                  className="noto-serif-kr w-full resize-none rounded-xl border border-stone-400 bg-white px-3 py-2.5 text-base leading-relaxed text-gray-900 outline-none"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="noto-serif-kr block w-full whitespace-pre-line break-keep rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-left text-base leading-relaxed text-gray-800"
                >
                  {message}
                </button>
              )}
              <div className="mt-1 text-right text-xs text-gray-400">
                {charCount}/{THANKS_MESSAGE_MAX_CHARS}자
              </div>
            </div>
          </div>

          {/* 3. 보내는 이 */}
          <div>
            <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-gray-800">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-stone-100 text-[11px] font-bold text-stone-600">
                3
              </span>
              보내는 이
            </p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
                placeholder="상주 이름"
                className="min-w-0 flex-1 rounded-xl border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-stone-500"
              />
              <div className="flex shrink-0 overflow-hidden rounded-xl border border-gray-300">
                {THANKS_SENDER_SUFFIXES.map((suffix) => (
                  <button
                    key={suffix}
                    type="button"
                    onClick={() => setSenderSuffix(suffix)}
                    className={`px-3 py-2.5 text-sm font-medium transition-colors ${
                      senderSuffix === suffix
                        ? 'bg-stone-600 text-white'
                        : 'bg-white text-gray-500'
                    }`}
                  >
                    {suffix}
                  </button>
                ))}
              </div>
            </div>
            <p className="mt-1.5 text-xs text-gray-400">
              인쇄 시{' '}
              <b className="font-semibold text-gray-600 underline decoration-1 underline-offset-2">
                {composedSender}
              </b>{' '}
              형태로 출력됩니다.
            </p>
          </div>
        </div>
      </div>
    );
  };
